import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog } from '../../ui/components/Dialog';
import { Button } from '../../ui/components/Button';
import { TextField } from '../../ui/components/TextField';
import { useSettings } from '../../hooks/useSettings';
import { useEmailConfirmation } from '../../hooks/useEmailConfirmation';
import { getNotificationPreferences, updateNotificationPreferences } from '../../api/settings';
import { FeatherMail, FeatherCheck, FeatherBellOff, FeatherX } from '@subframe/core';

interface PostRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  dropName: string;
  phaseName: string;
  mintStartAt: string | null;
}

export const PostRegistrationModal: React.FC<PostRegistrationModalProps> = ({
  isOpen,
  onClose,
  dropName,
  phaseName,
  mintStartAt,
}) => {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const {
    setEmail: submitEmail,
    isSettingEmail,
    setEmailError,
    resendConfirmation,
    isResending,
    resendSuccess,
  } = useEmailConfirmation();

  const [email, setEmail] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [mintRemindersEnabled, setMintRemindersEnabled] = useState<boolean | null>(null);
  const [isEnablingReminders, setIsEnablingReminders] = useState(false);

  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsValidEmail(emailRegex.test(email));
  }, [email]);

  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setEmailSubmitted(false);
      setMintRemindersEnabled(null);
      setIsEnablingReminders(false);
      getNotificationPreferences()
        .then(prefs => setMintRemindersEnabled(prefs.notify_on_mint_reminders))
        .catch(() => setMintRemindersEnabled(true));
    }
  }, [isOpen]);

  const enableMintReminders = async () => {
    setIsEnablingReminders(true);
    try {
      await updateNotificationPreferences({ notify_on_mint_reminders: true });
      setMintRemindersEnabled(true);
    } catch {
      // Silently fail — user can enable in settings later
    } finally {
      setIsEnablingReminders(false);
    }
  };

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValidEmail && email) {
      submitEmail(email, {
        onSuccess: () => {
          setEmailSubmitted(true);
          if (mintRemindersEnabled === false) {
            enableMintReminders();
          }
        },
      });
    }
  };

  const formatMintDate = (isoDate: string): string => {
    const date = new Date(isoDate);
    return date.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const hasConfirmedEmail = settings?.email && settings?.email_confirmed;
  const hasUnconfirmedEmail = settings?.email && !settings?.email_confirmed;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Content className="w-full max-w-[480px]">
        <div className="flex w-full flex-col items-start gap-5 p-6 relative">
          {/* Close button */}
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 flex h-8 w-8 items-center justify-center rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
            aria-label="Close"
          >
            <FeatherX className="h-5 w-5 text-subtext-color" />
          </button>

          {/* Celebration Header */}
          <div className="flex w-full flex-col items-center gap-3 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
              <FeatherCheck className="h-7 w-7 text-green-700 dark:text-green-400" />
            </div>
            <div className="flex flex-col items-center gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                You're on the list!
              </span>
              <span className="text-body font-body text-subtext-color">
                You're registered for {phaseName}.
              </span>
              {mintStartAt && (
                <span className="text-body font-body text-subtext-color">
                  Minting opens {formatMintDate(mintStartAt)}.
                </span>
              )}
            </div>
          </div>

          {/* Email Section */}
          {hasConfirmedEmail ? (
            mintRemindersEnabled === false ? (
              <div className="flex w-full flex-col items-center gap-3 rounded-lg border border-neutral-border bg-default-background p-4">
                <div className="flex items-center gap-2">
                  <FeatherBellOff className="h-5 w-5 text-subtext-color flex-shrink-0" />
                  <span className="text-body font-body text-default-font">
                    Mint reminders are currently off
                  </span>
                </div>
                <span className="text-caption font-caption text-subtext-color text-center">
                  Enable them to get notified at {settings.email} before minting opens.
                </span>
                <Button
                  variant="brand-secondary"
                  size="small"
                  onClick={enableMintReminders}
                  disabled={isEnablingReminders}
                >
                  {isEnablingReminders ? 'Enabling...' : 'Enable mint reminders'}
                </Button>
              </div>
            ) : (
              <div className="flex w-full items-center gap-3 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-4">
                <FeatherMail className="h-5 w-5 text-green-700 dark:text-green-400 flex-shrink-0" />
                <span className="text-body font-body text-green-700 dark:text-green-400">
                  We'll send mint reminders to {settings.email}
                </span>
              </div>
            )
          ) : hasUnconfirmedEmail ? (
            <div className="flex w-full flex-col gap-2 rounded-lg border border-neutral-border bg-default-background p-4">
              <div className="flex items-center gap-2">
                <FeatherMail className="h-5 w-5 text-brand-600 flex-shrink-0" />
                <span className="text-body font-body text-default-font">
                  Confirm your email to get mint reminders
                </span>
              </div>
              <span className="text-caption font-caption text-subtext-color">
                Check your inbox at {settings.email}
              </span>
              <Button
                variant="brand-tertiary"
                size="small"
                onClick={() => resendConfirmation()}
                disabled={isResending}
                className="self-start mt-1"
              >
                {isResending
                  ? 'Sending...'
                  : resendSuccess
                    ? 'Confirmation sent!'
                    : 'Resend confirmation'}
              </Button>
            </div>
          ) : emailSubmitted ? (
            <div className="flex w-full items-center gap-3 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-4">
              <FeatherMail className="h-5 w-5 text-green-700 dark:text-green-400 flex-shrink-0" />
              <span className="text-body font-body text-green-700 dark:text-green-400">
                Check your inbox to confirm at {email}
              </span>
            </div>
          ) : (
            <form onSubmit={handleEmailSubmit} className="flex w-full flex-col gap-3">
              <div className="flex w-full flex-col gap-1">
                <span className="text-body-bold font-body-bold text-default-font">
                  Get mint reminders
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  We'll email you 24h before, 1h before, and when minting goes live.
                </span>
              </div>

              {setEmailError && (
                <div className="w-full rounded border border-red-200 bg-red-50 px-4 py-3 text-body font-body text-red-700">
                  Failed to save email: {setEmailError.message}
                </div>
              )}

              <div className="flex w-full gap-2">
                <TextField className="h-auto w-full flex-1">
                  <TextField.Input
                    placeholder="your@email.com"
                    type="email"
                    value={email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                    autoFocus
                  />
                </TextField>
                <Button
                  type="submit"
                  disabled={!isValidEmail || isSettingEmail}
                  className="flex-shrink-0"
                >
                  {isSettingEmail ? '...' : 'Notify Me'}
                </Button>
              </div>
            </form>
          )}

          {/* Divider + Value Prop */}
          <div className="flex w-full flex-col gap-4 border-t border-neutral-border pt-4">
            <p className="text-caption font-caption text-subtext-color text-center">
              XRP Deals is the most advanced NFT marketplace on XRP.
            </p>
            <div className="flex w-full items-center justify-center gap-3">
              <Button
                variant="neutral-secondary"
                size="small"
                onClick={() => { navigate('/collections'); onClose(); }}
              >
                Browse collections
              </Button>
              <Button
                variant="neutral-secondary"
                size="small"
                onClick={() => { navigate('/launchpad'); onClose(); }}
              >
                Launch your own
              </Button>
            </div>
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};
